import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { BadgeCheck, Crown, Sparkles, CreditCard, Smartphone, Zap, Apple, Chrome, Landmark } from "lucide-react";
import { toast } from "sonner";
import { CURRENCIES } from "@/lib/constants";

const PLAN_META = [
  { id: "free", primary: false, gold: false, cta: "Register Free", sub: "Forever" },
  { id: "premium", primary: true, gold: false, cta: "Go Premium", sub: "/ month" },
  { id: "vip", primary: false, gold: true, cta: "Become VIP", sub: "/ month" },
];

function formatPrice(amount, symbol) {
  if (amount === 0) return `${symbol}0`;
  if (amount >= 100) {
    return `${symbol}${Math.round(amount).toLocaleString("en-IN")}`;
  }
  return `${symbol}${amount.toFixed(2)}`;
}

const METHOD_META = {
  card:        { label: "Card",        icon: CreditCard, sub: "Visa, Mastercard, Amex, RuPay" },
  upi:         { label: "UPI",         icon: Smartphone, sub: "GPay, PhonePe, Paytm" },
  pay_by_bank: { label: "Net Banking", icon: Landmark,   sub: "Direct bank transfer (HDFC, ICICI, SBI, Axis …)" },
  link:        { label: "Link",        icon: Zap,        sub: "Stripe Link — one-tap checkout" },
  apple_pay:   { label: "Apple Pay",   icon: Apple,      sub: "Auto-shown on iPhone, iPad, Mac" },
  google_pay:  { label: "Google Pay",  icon: Chrome,     sub: "Auto-shown on Chrome & Android" },
};

export default function Plans() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [plans, setPlans] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [credits, setCredits] = useState(null);
  const [rzpEnabled, setRzpEnabled] = useState(false);
  const [rzpLoading, setRzpLoading] = useState(null);
  const [creditLoading, setCreditLoading] = useState(null);

  // Initial currency: user.preferred_currency → derived from country → null (server picks)
  useEffect(() => {
    if (user?.preferred_currency) setCurrency(user.preferred_currency);
  }, [user?.preferred_currency]);

  // Razorpay configured?  Also lazy-load the Checkout.js script.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/razorpay/config");
        setRzpEnabled(!!data.enabled);
        if (data.enabled && !document.getElementById("razorpay-checkout-js")) {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.id = "razorpay-checkout-js";
          s.async = true;
          document.body.appendChild(s);
        }
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const qp = currency
          ? `?currency=${encodeURIComponent(currency)}`
          : user?.country
            ? `?country=${encodeURIComponent(user.country)}`
            : "";
        const [plansRes, creditsRes] = await Promise.all([
          api.get(`/plans${qp}`),
          api.get(`/credits/packs${qp}`),
        ]);
        setPlans(plansRes.data);
        setCredits(creditsRes.data);
        if (!currency && plansRes.data?.currency) setCurrency(plansRes.data.currency);
      } catch { /* keep null; UI shows skeleton */ }
    })();
  }, [user, currency]);

  const onCurrencyChange = async (newCur) => {
    setCurrency(newCur);
    if (user) {
      try {
        await api.put("/profile/me", { preferred_currency: newCur });
        await refresh();
        toast.success(`Display currency set to ${newCur.toUpperCase()}`);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
  };

  const subscribe = async (plan_id) => {
    if (!user) { navigate("/login"); return; }
    if (plan_id === "free") return;
    setLoading(plan_id);
    try {
      const { data } = await api.post("/checkout/session", {
        plan_id,
        origin_url: window.location.origin,
        currency: currency || undefined,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(null); }
  };

  const buyPack = async (packId) => {
    if (!user) { navigate("/login"); return; }
    setCreditLoading(packId);
    try {
      const { data } = await api.post("/credits/checkout", {
        pack_id: packId,
        origin_url: window.location.origin,
        currency: currency || undefined,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreditLoading(null); }
  };

  const payWithRazorpay = async (plan_id) => {
    if (!user) { navigate("/login"); return; }
    if (!window.Razorpay) {
      toast.error("Razorpay is still loading. Please try again in a moment.");
      return;
    }
    setRzpLoading(plan_id);
    try {
      const { data } = await api.post("/razorpay/order", { plan_id });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: data.name,
        description: data.description,
        order_id: data.order_id,
        prefill: data.prefill,
        theme: { color: "#7A1C28" },
        handler: async (resp) => {
          try {
            await api.post("/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            await refresh();
            toast.success("Payment successful — welcome to your new plan!");
            navigate("/dashboard");
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Payment verification failed");
          }
        },
        modal: {
          ondismiss: () => setRzpLoading(null),
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRzpLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="plans-page">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center">
          <p className="overline">Membership</p>
          <h1 className="font-serif text-5xl text-brand-ink mt-3">Find your forever, faster</h1>
          <p className="text-brand-inkLight mt-4 max-w-xl mx-auto">Choose a plan that fits your journey. Cancel anytime.</p>

          <div className="mt-6 inline-flex items-center gap-3 bg-white border border-brand-line rounded-full px-4 py-2 shadow-sm">
            <span className="text-[11px] uppercase tracking-[0.2em] text-brand-inkMute">Display currency</span>
            <select
              value={currency || ""}
              onChange={(e) => onCurrencyChange(e.target.value)}
              className="bg-transparent text-sm font-medium text-brand-ink focus:outline-none cursor-pointer"
              data-testid="plans-currency-select"
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{c.symbol} {c.code.toUpperCase()} — {c.name}</option>
              ))}
            </select>
          </div>

          {plans?.currency_code && (
            <p className="text-xs text-brand-inkMute mt-3 tracking-[0.2em] uppercase" data-testid="plans-currency-pill">
              Prices in {plans.currency_code}{user?.country ? ` · for ${user.country}` : ""}
            </p>
          )}

          {(plans?.payment_methods?.length > 0 || plans?.wallets?.length > 0) && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2" data-testid="plans-payment-methods">
              {[...(plans?.payment_methods || []), ...(plans?.wallets || [])].map((m) => {
                const meta = METHOD_META[m] || { label: m.toUpperCase(), icon: CreditCard, sub: "" };
                const Icon = meta.icon;
                return (
                  <span key={m} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-brand-line rounded-full text-xs text-brand-ink shadow-sm" title={meta.sub} data-testid={`payment-method-${m}`}>
                    <Icon className="h-3.5 w-3.5 text-brand-maroon" strokeWidth={1.6} />
                    {meta.label}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-16">
          {PLAN_META.map((meta) => {
            const plan = plans?.plans?.[meta.id];
            const isCurrent = user?.plan === meta.id;
            const symbol = plans?.symbol || "₹";
            const price = plan ? formatPrice(plan.amount, symbol) : "—";
            return (
              <div key={meta.id} className={`relative p-8 rounded-lg bg-white border ${meta.primary ? "border-brand-maroon shadow-2xl md:-translate-y-3" : meta.gold ? "border-brand-gold shadow-xl" : "border-brand-line shadow-sm"} transition-all`} data-testid={`plan-${meta.id}`}>
                {meta.primary && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-maroon text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full">Most Popular</span>}
                {meta.gold && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-gold text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full">Exclusive</span>}
                <div className="flex items-center gap-2 mb-1">
                  {meta.primary && <Sparkles className="h-4 w-4 text-brand-maroon" />}
                  {meta.gold && <Crown className="h-4 w-4 text-brand-gold" />}
                  <p className="overline">{plan?.name || meta.id}</p>
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-serif text-5xl text-brand-ink">{price}</span>
                  <span className="text-sm text-brand-inkLight">{meta.sub}</span>
                </div>
                {plan?.usd_equivalent > 0 && plans?.currency !== "usd" && (
                  <p className="mt-1 text-xs text-brand-inkMute" data-testid={`usd-equiv-${meta.id}`}>
                    ≈ ${plan.usd_equivalent.toFixed(2)} USD <span className="text-brand-inkMute/70">· auto-converted by your bank</span>
                  </p>
                )}
                <ul className="mt-6 space-y-3">
                  {(plan?.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-brand-ink"><BadgeCheck className="h-4 w-4 text-brand-gold mt-0.5" /> {f}</li>
                  ))}
                </ul>
                <button
                  onClick={() => subscribe(meta.id)}
                  disabled={isCurrent || loading === meta.id || meta.id === "free"}
                  className={`w-full mt-8 ${meta.primary ? "btn-primary" : meta.gold ? "btn-gold" : "btn-ghost"} disabled:opacity-60`}
                  data-testid={`subscribe-${meta.id}`}
                >
                  {isCurrent ? "Current Plan" : loading === meta.id ? "Redirecting..." : meta.cta}
                </button>
                {plans?.currency === "inr" && meta.id !== "free" && !isCurrent && (
                  <button
                    onClick={() => payWithRazorpay(meta.id)}
                    disabled={!rzpEnabled || rzpLoading === meta.id}
                    className="w-full mt-2 text-xs border border-brand-line text-brand-ink py-2 rounded-md hover:bg-brand-muted transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={rzpEnabled ? "Native UPI / Net Banking / Wallets via Razorpay" : "Razorpay is not yet configured (admin: add RAZORPAY_KEY_ID to .env)"}
                    data-testid={`razorpay-${meta.id}`}
                  >
                    {rzpLoading === meta.id ? "Opening Razorpay..." : rzpEnabled ? "Or pay with Razorpay (UPI / Wallets)" : "Razorpay — coming soon"}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {plans?.currency_code && (
          <p className="text-center text-xs text-brand-inkMute mt-8 max-w-2xl mx-auto leading-relaxed" data-testid="plans-fx-disclaimer">
            <Sparkles className="inline h-3 w-3 mr-1 text-brand-gold align-text-bottom" />
            You will be charged in <span className="font-medium text-brand-ink">{plans.currency_code}</span> via {[...(plans.payment_methods || []), ...(plans.wallets || [])].map((m) => METHOD_META[m]?.label || m).join(", ") || "card"}. If this isn&apos;t your card&apos;s home currency, your bank may apply its own conversion rate and a small foreign-exchange fee.
          </p>
        )}

        {credits?.packs?.length > 0 && (
          <div id="credit-packs-section" className="mt-20 pt-12 border-t border-brand-line scroll-mt-24" data-testid="credit-packs-section">
            <div className="text-center">
              <p className="overline">Pay-as-you-go</p>
              <h2 className="font-serif text-4xl text-brand-ink mt-3">Buy interest credits</h2>
              <p className="text-brand-inkLight mt-3 max-w-xl mx-auto text-sm">
                Not ready for a monthly plan? Buy a pack of interest credits. One credit unlocks one interest send. Credits never expire.
              </p>
              {user && (
                <p className="mt-4 inline-flex items-center gap-2 bg-brand-gold/10 border border-brand-gold/40 text-brand-goldDark px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] font-semibold" data-testid="credits-balance">
                  <Sparkles className="h-3 w-3" /> Your balance: {user.interest_credits || 0} credits
                </p>
              )}
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-10">
              {credits.packs.map((p) => (
                <div key={p.id} className={`relative p-6 rounded-lg bg-white border ${p.best_value ? "border-brand-gold shadow-xl" : "border-brand-line shadow-sm"} transition-all`} data-testid={`credit-pack-${p.id}`}>
                  {p.best_value && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-gold text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full">Best value</span>}
                  <p className="overline">{p.name}</p>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-serif text-4xl text-brand-ink">{credits.symbol}{p.amount >= 100 ? Math.round(p.amount).toLocaleString("en-IN") : p.amount.toFixed(2)}</span>
                  </div>
                  <p className="text-sm text-brand-inkLight mt-1">{p.credits} interest credits</p>
                  <p className="text-[11px] text-brand-inkMute mt-1">
                    {credits.symbol}{(p.per_credit_amount >= 100 ? Math.round(p.per_credit_amount).toLocaleString("en-IN") : p.per_credit_amount.toFixed(2))} per credit
                    {credits.currency !== "usd" && <> · ≈ ${p.usd_equivalent.toFixed(2)} USD</>}
                  </p>
                  <button
                    onClick={() => buyPack(p.id)}
                    disabled={creditLoading === p.id}
                    className={`w-full mt-6 ${p.best_value ? "btn-gold" : "btn-ghost"} disabled:opacity-60 text-sm py-2.5`}
                    data-testid={`buy-pack-${p.id}`}
                  >
                    {creditLoading === p.id ? "Redirecting…" : `Buy ${p.credits} credits`}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
